/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { createContext, useContext } from "react";
import type { DirectoryConfig, DirectoryContextValue, UseDirectoryResult } from "./Directory.types";

export const DirectoryContext = createContext<DirectoryContextValue | null>(null);

/**
 * Read the raw Directory context value. Generic is erased at the context
 * boundary — for typed access to `result.visibleItems` / `result.filteredItems`,
 * prefer `useDirectoryItems<T>()` or `useDirectoryConfig<T>()`.
 */
export function useDirectoryContext(): DirectoryContextValue {
	const ctx = useContext(DirectoryContext);
	if (!ctx) {
		throw new Error(
			"[Directory] Compound component slot was rendered outside a <Directory> root. " +
				"Wrap Directory.Heading, Directory.Filters, Directory.Grid, Directory.Pagination, " +
				"and Directory.NoResults inside a single <Directory> element.",
		);
	}
	return ctx;
}

/**
 * Typed view of the Directory result, with the generic `<T>` restored at the
 * call site. Use this in custom slot components instead of repeating the
 * `as T[]` cast pattern that the built-in `DirectoryGrid` uses internally.
 *
 * ```tsx
 * function CustomList<T>(): React.JSX.Element {
 *   const { visibleItems } = useDirectoryItems<T>();
 *   return <ul>{visibleItems.map(...)}</ul>;
 * }
 * ```
 */
export function useDirectoryItems<T>(): UseDirectoryResult<T> {
	const ctx = useDirectoryContext();
	return ctx.result as unknown as UseDirectoryResult<T>;
}

/**
 * Typed view of the Directory config. Pair with `useDirectoryItems` when
 * composing a custom slot — e.g. to read `config.getKey` for stable keys.
 */
export function useDirectoryConfig<T>(): DirectoryConfig<T> {
	const ctx = useDirectoryContext();
	return ctx.config as unknown as DirectoryConfig<T>;
}

/**
 * Current search query — value of the FIRST `search` filter in the config,
 * or `""` if no search filter is wired or nothing has been typed. Shared by
 * `DirectoryGrid` (for `renderEmpty(query)`) and `DirectoryNoResults` (for
 * the "No results for {query}" message) so the rule lives in one place.
 */
export function useCurrentSearchQuery(): string {
	const { config, result } = useDirectoryContext();
	const searchKey = config.filters.find((f) => f.kind === "search")?.paramKey;
	return searchKey ? (result.state.searchQueries[searchKey] ?? "") : "";
}
